import {
  AddressInput,
  Button,
  Checkbox,
  CountryInput,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FieldWrapper,
  Input,
  Label,
  PhoneInput,
  Progress,
} from '@commenda/kakashi';
import { IAddress } from '@commenda/types';
import { DirectorBase } from '@commenda/types/src/incorporation/types';
import { zodResolver } from '@hookform/resolvers/zod';
import { FieldError, useForm } from 'react-hook-form';
import { personalDetailsSchema, TPersonalDetailFields } from './schema';

type TPersonalInfoForm = {
  onNext: (formData?: TPersonalDetailFields) => void;
  director?: DirectorBase | null;
  onBack: () => void;
  defaultValues: TPersonalDetailFields;
};

export const PersonalInfoForm = ({
  onNext,
  onBack,
  defaultValues,
}: TPersonalInfoForm) => {
  const {
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    clearErrors,
  } = useForm<TPersonalDetailFields>({
    resolver: zodResolver(personalDetailsSchema),
    defaultValues,
  });

  const onSubmit = (formData: TPersonalDetailFields) => {
    onNext(formData);
  };

  const personalDetails = watch('personalDetails') as {
    nationality: string;
    isPoliticallyExposed: boolean;
  };

  const personalDetailsError = errors?.personalDetails as {
    nationality: FieldError;
    isPoliticallyExposed: FieldError;
  };

  const operatingAddressError = errors?.operatingAddress as {
    country: FieldError;
    addressLine1: FieldError;
    addressLine2: FieldError;
    city: FieldError;
    postalCode: FieldError;
    state: FieldError;
  };

  return (
    <div className="max-h-[85vh] overflow-hidden flex flex-col">
      <DialogHeader className="px-6 py-5 border-none">
        <DialogTitle>Personal Details</DialogTitle>
      </DialogHeader>
      <div className="flex gap-2">
        <Progress className="rounded-full h-1" accent="default" value={100} />
        <Progress className="rounded-full h-1" />
        <Progress className="rounded-full h-1" />
      </div>
      <div className="grow overflow-hidden">
        <div className="p-6 flex flex-col gap-4 h-full overflow-y-auto">
          <FieldWrapper>
            <Label className="text-sm gap-0.5 text-neutral-1">
              <span>Legal Name</span>
              <span>*</span>
            </Label>
            <div className="grid grid-cols-2 gap-2">
              <Input
                placeholder="First name"
                onChange={(e) => {
                  setValue('directorFirstName', e.target.value);
                  clearErrors('directorFirstName');
                }}
                error={errors?.directorFirstName?.message}
                value={watch('directorFirstName')}
              />
              <Input
                placeholder="Last name"
                error={errors?.directorFirstName?.message}
                onChange={(e) => {
                  setValue('directorLastName', e.target.value);
                  clearErrors('directorLastName');
                }}
                value={watch('directorLastName')}
              />
            </div>
          </FieldWrapper>

          <div className="grid grid-cols-2 gap-2">
            <FieldWrapper>
              <Label className="text-sm text-neutral-1">
                Phone number
                <span className="text-text-violet-tertiary ml-1">*</span>
              </Label>

              <PhoneInput
                id="phoneNumber"
                placeholder=""
                className="h-[42px]"
                onChange={(e) => {
                  setValue('number', e);
                  clearErrors('number');
                }}
                value={watch('number')}
                error={errors?.number?.message}
              />
            </FieldWrapper>

            <FieldWrapper>
              <Label className="text-sm text-neutral-1">
                Email <span className="text-violet-solid ml-1">*</span>
              </Label>

              <Input
                type="email"
                placeholder="Email"
                error={errors?.email?.message}
                value={watch('email')}
                onChange={(e) => {
                  setValue('email', e.target.value);
                  clearErrors('email');
                }}
              />
            </FieldWrapper>
          </div>

          <FieldWrapper>
            <Label className="text-sm text-neutral-1">
              Nationality<span className="text-violet-solid ml-1">*</span>
            </Label>

            <CountryInput
              placeholder="Search country"
              type="single"
              onValueChange={(ctr) => {
                setValue('personalDetails.nationality', ctr);
                clearErrors('personalDetails.nationality');
              }}
              error={
                personalDetailsError?.nationality?.message &&
                !watch('personalDetails.nationality')
                  ? personalDetailsError?.nationality?.message
                  : undefined
              }
              value={watch('personalDetails.nationality')}
              _className="overflow-hidden"
            />
          </FieldWrapper>

          <FieldWrapper>
            <Label>
              Current Address <span className="text-violet-solid ml-1">*</span>
            </Label>
            <AddressInput
              address={watch('operatingAddress') as IAddress}
              onChange={(ad) => {
                setValue('operatingAddress', ad);
                if (ad.country !== watch('operatingAddress.country')) {
                  clearErrors('operatingAddress.state');
                }
              }}
              lastRowClassName="grid-cols-2"
              error={{
                country:
                  operatingAddressError?.country?.message &&
                  !watch('operatingAddress.country')
                    ? operatingAddressError?.country?.message
                    : undefined,
                addressLine1:
                  !watch('operatingAddress.addressLine1') &&
                  operatingAddressError?.addressLine1?.message
                    ? operatingAddressError?.addressLine1?.message
                    : null,
                addressLine2:
                  !watch('operatingAddress.addressLine2') &&
                  operatingAddressError?.addressLine2?.message
                    ? operatingAddressError?.addressLine2?.message
                    : null,
                city:
                  !watch('operatingAddress.city') &&
                  operatingAddressError?.city?.message
                    ? operatingAddressError?.city?.message
                    : null,
                postalCode:
                  !watch('operatingAddress.postalCode') &&
                  operatingAddressError?.postalCode?.message
                    ? operatingAddressError?.postalCode?.message
                    : null,
                state:
                  !watch('operatingAddress.state') &&
                  operatingAddressError?.state?.message
                    ? operatingAddressError?.state?.message
                    : undefined,
              }}
            />
          </FieldWrapper>

          <FieldWrapper>
            <div className="flex gap-1.5 items-center">
              <Checkbox
                id="isPoliticallyExposed"
                checked={watch('personalDetails.isPoliticallyExposed')}
                onCheckedChange={(e) =>
                  setValue(
                    'personalDetails.isPoliticallyExposed',
                    !watch('personalDetails.isPoliticallyExposed')
                  )
                }
              />
              <Label htmlFor="isPoliticallyExposed">
                I certify that this individual is not a politically exposed
                person (PEP)
              </Label>
            </div>
          </FieldWrapper>
        </div>
      </div>
      <DialogFooter>
        <Button size="sm" variant="secondary" onClick={onBack}>
          Cancel
        </Button>
        <Button size="sm" onClick={handleSubmit(onSubmit)}>
          Next
        </Button>
      </DialogFooter>
    </div>
  );
};
